import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../lib/axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, Calendar, Building2, Users, Stethoscope, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { formatTime12h } from '../lib/utils';

export default function BookAppointment() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [hospitals, setHospitals] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [slots, setSlots] = useState([]);
  
  const [formData, setFormData] = useState({
    hospital_id: '',
    department_id: '',
    service_id: '',
    doctor_id: '',
    appointment_date: '',
    appointment_time: '',
    consultation_type: 'in-person',
    patient_info: {}
  });
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchHospitals();
  }, []);

  const fetchHospitals = async () => {
    try {
      const res = await axiosInstance.get('/hospitals');
      setHospitals(res.data);
    } catch (error) {
      toast.error('Failed to load hospitals');
    }
  };

  const fetchDepartments = async (hospitalId) => {
    try {
      const res = await axiosInstance.get(`/departments?hospital_id=${hospitalId}`);
      setDepartments(res.data);
    } catch (error) {
      toast.error('Failed to load departments');
    }
  };

  const fetchServices = async (departmentId) => {
    try {
      const res = await axiosInstance.get(`/services?department_id=${departmentId}`);
      setServices(res.data);
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  const fetchDoctors = async (departmentId) => {
    try {
      const res = await axiosInstance.get(`/doctors?department_id=${departmentId}`);
      setDoctors(res.data);
    } catch (error) {
      toast.error('Failed to load doctors');
    }
  };

  const fetchSlots = async (doctorId, date) => {
    try {
      const res = await axiosInstance.get(`/slots/available?doctor_id=${doctorId}&date=${date}`);
      setSlots(res.data);
    } catch (error) {
      toast.error('Failed to load available slots');
    }
  };

  const handleHospitalSelect = (hospitalId) => {
    setFormData({...formData, hospital_id: hospitalId, department_id: '', service_id: '', doctor_id: ''});
    fetchDepartments(hospitalId);
    setStep(2);
  };

  const handleDepartmentSelect = (departmentId) => {
    setFormData({...formData, department_id: departmentId, service_id: '', doctor_id: ''});
    fetchServices(departmentId);
    fetchDoctors(departmentId);
    setStep(3);
  };

  const handleServiceSelect = (serviceId) => {
    setFormData({...formData, service_id: serviceId});
    setStep(4);
  };

  const handleDoctorSelect = (doctorId) => {
    setFormData({...formData, doctor_id: doctorId});
    setStep(5);
  };

  const handleDateSelect = (date) => {
    setFormData({...formData, appointment_date: date});
    if (formData.doctor_id) {
      fetchSlots(formData.doctor_id, date);
    }
  };

  const handleSubmit = async () => {
    if (!formData.appointment_time) {
      toast.error('Please select a time slot');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        patient_id: user.email,
        ...formData
      };
      
      await axiosInstance.post('/appointments', payload);
      toast.success('Appointment booked successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Hospital', icon: Building2 },
    { number: 2, title: 'Department', icon: Users },
    { number: 3, title: 'Service', icon: Stethoscope },
    { number: 4, title: 'Doctor', icon: Users },
    { number: 5, title: 'Time Slot', icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-3xl font-manrope font-bold text-center mb-8" data-testid="booking-title">
          Book an Appointment
        </h1>

        {/* Progress Steps */}
        <div className="flex justify-between mb-12">
          {steps.map((s, index) => {
            const Icon = s.icon;
            const isActive = step === s.number;
            const isCompleted = step > s.number;
            
            return (
              <div key={s.number} className="flex flex-col items-center flex-1" data-testid={`step-${s.number}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  isCompleted ? 'bg-primary text-white' :
                  isActive ? 'bg-primary text-white' :
                  'bg-slate-200 text-slate-500'
                }`}>
                  {isCompleted ? <CheckCircle className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </div>
                <span className={`text-xs font-medium ${
                  isActive || isCompleted ? 'text-primary' : 'text-slate-500'
                }`}>
                  {s.title}
                </span>
                {index < steps.length - 1 && (
                  <div className={`h-0.5 w-full mt-5 -mx-2 absolute ${
                    isCompleted ? 'bg-primary' : 'bg-slate-200'
                  }`} style={{top: '20px', zIndex: -1}} />
                )}
              </div>
            );
          })}
        </div>

        <Card className="border-2 border-slate-200">
          <CardHeader>
            <CardTitle data-testid="current-step-title">{steps[step - 1]?.title}</CardTitle>
            <CardDescription>Select an option to continue</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Select Hospital */}
            {step === 1 && (
              <div className="space-y-4" data-testid="hospital-selection">
                {hospitals.map((hospital, index) => (
                  <Card
                    key={index}
                    className="cursor-pointer card-hover border-2 hover:border-primary"
                    onClick={() => handleHospitalSelect(hospital.id || hospital.name)}
                    data-testid={`hospital-option-${index}`}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg">{hospital.name}</CardTitle>
                      <CardDescription>{hospital.address}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
                {hospitals.length === 0 && (
                  <Alert>
                    <AlertDescription>No hospitals available. Please contact admin.</AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Step 2: Select Department */}
            {step === 2 && (
              <div className="space-y-4" data-testid="department-selection">
                {departments.map((dept, index) => (
                  <Card
                    key={index}
                    className="cursor-pointer card-hover border-2 hover:border-primary"
                    onClick={() => handleDepartmentSelect(dept.id || dept.name)}
                    data-testid={`department-option-${index}`}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg">{dept.name}</CardTitle>
                      <CardDescription>{dept.description}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}

            {/* Step 3: Select Service */}
            {step === 3 && (
              <div className="space-y-4" data-testid="service-selection">
                {services.map((service, index) => (
                  <Card
                    key={index}
                    className="cursor-pointer card-hover border-2 hover:border-primary"
                    onClick={() => handleServiceSelect(service.id || service.name)}
                    data-testid={`service-option-${index}`}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center justify-between">
                        {service.name}
                        <span className="text-primary">₹{service.price}</span>
                      </CardTitle>
                      <CardDescription>
                        {service.description} • {service.duration} minutes
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}

            {/* Step 4: Select Doctor */}
            {step === 4 && (
              <div className="space-y-4" data-testid="doctor-selection">
                <div className="mb-4">
                  <Label>Consultation Type</Label>
                  <Select
                    value={formData.consultation_type}
                    onValueChange={(value) => setFormData({...formData, consultation_type: value})}
                  >
                    <SelectTrigger data-testid="consultation-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in-person">In-Person</SelectItem>
                      <SelectItem value="video">Video Consultation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {doctors.map((doctor, index) => (
                  <Card
                    key={index}
                    className="cursor-pointer card-hover border-2 hover:border-primary"
                    onClick={() => handleDoctorSelect(doctor.id || doctor.user_id)}
                    data-testid={`doctor-option-${index}`}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg">Dr. {doctor.user_id}</CardTitle>
                      <CardDescription>{doctor.specialization}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}

            {/* Step 5: Select Date & Time */}
            {step === 5 && (
              <div className="space-y-6" data-testid="datetime-selection">
                <div className="space-y-2">
                  <Label htmlFor="date">Select Date</Label>
                  <Input
                    id="date"
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.appointment_date}
                    onChange={(e) => handleDateSelect(e.target.value)}
                    data-testid="date-input"
                  />
                </div>

                {formData.appointment_date && (
                  <div className="space-y-2">
                    <Label>Available Time Slots</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {slots.filter(s => s.available).map((slot, index) => (
                        <Button
                          key={index}
                          variant={formData.appointment_time === slot.time ? "default" : "outline"}
                          onClick={() => setFormData({...formData, appointment_time: slot.time})}
                          data-testid={`time-slot-${index}`}
                        >
                          {formatTime12h(slot.time)}
                        </Button>
                      ))}
                    </div>
                    {slots.length === 0 && (
                      <Alert>
                        <AlertDescription>No slots available for this date.</AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}

                <Button
                  className="w-full"
                  onClick={handleSubmit}
                  disabled={!formData.appointment_time || loading}
                  data-testid="confirm-booking-button"
                >
                  {loading ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </div>
            )}

            {/* Back Button */}
            {step > 1 && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setStep(step - 1)}
                data-testid="back-button"
              >
                Back
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}